import { warn } from '@execonline-inc/logging';
import { InvalidUrlError } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { Task } from 'taskarian';
import BulkEditInvitationsStore from '.';
import { AppyError, callApi } from '../../../../../../../Appy';
import { handleError } from '../../../../../../../ErrorActionableReaction';
import { MissingLinkError, findLinkT } from '../../../../../../../LinkyLinky';
import ReactionComponent from '../../../../../../../ReactionComponent';
import { Link } from '../../../../../../../Resource/Types';
import { bulkInvitationsResourceDecoder } from './Decoders';
import { bulkEditInvitationsEncoder } from './Encoders';
import { Deactivating, Reactivating, State } from './Types';

interface Props {
  store: BulkEditInvitationsStore;
}

export type BulkEditInvitationsError = AppyError | InvalidUrlError | MissingLinkError;

export const handleBulkEditInvitationsError =
  (store: BulkEditInvitationsStore) => (error: BulkEditInvitationsError) => {
    switch (error.kind) {
      case 'missing-link-error':
      case 'invalid-url-error':
        store.error('We are unable to process your request at this time');
        warn('ParticipantsReactions', error.kind, error);
        break;
      default:
        handleError(store, error);
    }
  };

export const reactivate = (store: BulkEditInvitationsStore, state: Reactivating) =>
  Task.succeed<BulkEditInvitationsError, ReadonlyArray<Link>>(store.invitationsStore.links)
    .andThen(findLinkT('reactivate'))
    .andThen(callApi(bulkInvitationsResourceDecoder, bulkEditInvitationsEncoder(store, state)))
    .fork(handleBulkEditInvitationsError(store), (resource) => {
      store.invitationsStore.updateInvitations(resource);
      store.reactivateSucceeded(resource);
    });

export const deactivate = (store: BulkEditInvitationsStore, state: Deactivating) =>
  Task.succeed<BulkEditInvitationsError, ReadonlyArray<Link>>(store.invitationsStore.links)
    .andThen(findLinkT('deactivate'))
    .andThen(callApi(bulkInvitationsResourceDecoder, bulkEditInvitationsEncoder(store, state)))
    .fork(handleBulkEditInvitationsError(store), (resource) => {
      store.invitationsStore.updateInvitations(resource);
      store.deactivateSucceeded(resource);
    });

class BulkEditInvitationsReactions extends ReactionComponent<
  BulkEditInvitationsStore,
  State,
  Props
> {
  tester = () => this.props.store.state;
  effect = (state: State) => {
    const { store } = this.props;
    switch (state.kind) {
      case 'deactivating':
        deactivate(store, state);
        break;
      case 'reactivating':
        reactivate(store, state);
        break;
      case 'waiting':
      case 'confirming-deactivate':
      case 'error':
      case 'deactivate-succeeded':
      case 'reactivate-succeeded':
        break;
      default:
        assertNever(state);
    }
  };
}

export default BulkEditInvitationsReactions;
